import { Country } from '@tiendanube/common/src/enums';
import { NUVEMPAGO_ENABLED } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { useRoles } from 'domains/Auth/authSlice/useRoles';
import { useGetCountry, useIsLogged } from 'domains/Auth/hooks';
import { useNuvemPagoFeatureFlagQuery } from '../nuvemPagoCommonsApi';

const AVAILABLE_NUVEMPAGO_COUNTRIES = [Country.BR, Country.AR, Country.MX];
const DEFAULT_CACHE_TTL = 120; // 2 minutes

// This is a workaround for typing query options, as the Redux Toolkit package
// does not export the `UseQuerySubscriptionOptions` interface and it is nearly
// impossible to extract usable types from the generated hook signatures due to
// the deep generics nesting.
interface QuerySubscriptionOptions {
  skip?: boolean;
  refetchOnMountOrArgChange?: boolean | number;
  pollingInterval?: number;
  refetchOnReconnect?: boolean;
  refetchOnFocus?: boolean;
}

/**
 * A hook to check whether the store has a Nuvem Pago feature flag enabled.
 *
 * Accepts RTK query subscription options, with the following special cases:
 * - `skip`: If `undefined`, a sensitive default will be used based on the user
 * login state, roles, country, and Nuvem Pago tags to define whether the query
 * should be fired. Any other value explicitly provided will be applied.
 * - `refetchOnMountOrArgChange`: If `undefined`, caches the result for to 2min.
 * This is a measure to be able to react quickly to rule changes in the flag or
 * an eventual deactivation, in addition to avoiding triggering multiple
 * requests when there is more than one consumer per page. This behavior can be
 * changed by explicitly passing any acceptable value to this option.
 *
 * Returns the RTK query result object with the additional properties:
 * `featureFlag`, which is the flag being checked; and `isFeatureEnabled`,
 * a boolean indicating whether the feature is enabled when the query is
 * resolved, `false` in case of errors, or `null` for any other query state.
 */
export function useNuvemPagoFeatureFlag(
  featureFlag: string,
  options: QuerySubscriptionOptions = {},
) {
  const storeCountry = useGetCountry();
  const { isLogin } = useIsLogged();
  const { validateRoles } = useRoles();
  const hasFullRole = validateRoles(['full', 'payments']);
  const [hasNuvempagoEnabled] = useHasTags(NUVEMPAGO_ENABLED);
  const availableNuvempago =
    AVAILABLE_NUVEMPAGO_COUNTRIES.includes(storeCountry);
  const shouldHasNuvempagoTag =
    storeCountry === Country.BR ? true : hasNuvempagoEnabled;

  const requireFetch =
    isLogin && shouldHasNuvempagoTag && availableNuvempago && hasFullRole;

  const skip = options.skip ?? !requireFetch;
  const refetchOnMountOrArgChange =
    options.refetchOnMountOrArgChange ?? DEFAULT_CACHE_TTL;

  const queryResult = useNuvemPagoFeatureFlagQuery(featureFlag, {
    ...options,
    skip,
    refetchOnMountOrArgChange,
  });

  let isFeatureEnabled: boolean | null;

  if (queryResult.isSuccess) {
    isFeatureEnabled = queryResult.data as boolean;
  } else {
    isFeatureEnabled = queryResult.isError ? false : null;
  }

  return {
    ...queryResult,
    featureFlag,
    isFeatureEnabled,
  };
}
